<div [formGroup]="form" class="flex flex-col gap-4">
  <div class="flex flex-col gap-4" *ngIf="isFirstStep">
    <div>
      <p-message severity="info" icon="fa-solid fa-info-circle text-20">
        <div class="w-full flex justify-between items-center">
          <p class="text-13">
            Please enter as much information about the project as you can. Note that you will be required to enter the "parent project" - the name of the
            umbrella if this project is part of an umbrella - for the new project. From the information you enter, a "draft" project will be created and a
            notice will be sent to the formation team. The formation team will then review this project and reach out to you with any questions or draft
            governance materials for your review. If at any point you have any questions, please reach out to
            <a href="mailto:formation@linuxfoundation.org" target="_blank">formation&#64;linuxfoundation.org</a>.
          </p>
        </div>
      </p-message>
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-9">
      <div class="project-detail">
        <label
          >Common Name <span class="text-red" *ngIf="fieldRequired('Name')">*</span>
          <i
            class="fa-regular fa-info-circle ml-1"
            appendTo="body"
            [pTooltip]="commonNameTooltip"
            tooltipPosition="top"
            [escape]="false"
            [autoHide]="false"></i>
        </label>
        <input type="text" pInputText formControlName="Name" />
        <small *ngIf="hasError('Name')" class="text-xs text-red">{{ getError('Name') }}</small>
      </div>
      <div class="project-detail">
        <label
          >Category
          <i class="fa-regular fa-info-circle" appendTo="body" [pTooltip]="categoryTooltip" tooltipPosition="top" [escape]="false" [autoHide]="false"></i>
        </label>
        <ng-container *ngIf="categories$ | async as categories; else inputLoading">
          <p-select appendTo="body" [options]="categories" formControlName="Category" placeholder="Select category"></p-select>
        </ng-container>
      </div>
      <div class="project-detail">
        <label
          >Slug <span class="text-red" *ngIf="fieldRequired('Slug')">*</span>
          <i class="fa-regular fa-info-circle ml-1" appendTo="body" [pTooltip]="slugTooltip" tooltipPosition="top" [escape]="false" [autoHide]="false"></i>
        </label>
        <div class="flex gap-2 items-center">
          <input type="text" pInputText formControlName="Slug" class="flex-grow" />
          <a class="text-xs cursor-pointer" (click)="onReset()" *ngIf="customSlug">Reset</a>
        </div>
        <small *ngIf="hasError('Slug')" class="text-xs text-red">{{ getError('Slug') }}</small>
      </div>
      <div class="project-detail">
        <div class="flex flex-col lg:flex-row justify-between gap-2">
          <label>Parent Project <span class="text-red" *ngIf="fieldRequired('Parent')">*</span></label>
          <div class="flex items-center gap-1">
            <label class="cursor-pointer flex items-center gap-2">
              <p-checkbox
                (onChange)="onCheckNoParent($event)"
                [binary]="true"
                [(ngModel)]="disableParentProject"
                [ngModelOptions]="{ standalone: true }"></p-checkbox>
              No Parent</label
            >
            <i class="fa-regular fa-info-circle" appendTo="body" [pTooltip]="noParentTooltip" tooltipPosition="top" [escape]="false" [autoHide]="false"></i>
          </div>
        </div>
        <p-autocomplete
          formControlName="Parent"
          [suggestions]="parentProjectOptions"
          (completeMethod)="handleParentSearch($event)"
          appendTo="body"
          [delay]="500"
          dataKey="ID"
          optionLabel="Name"
          placeholder="Search for a project"
          panelStyleClass="legal-dialog-dropdown">
          <ng-template let-project #item>
            <div class="flex align-items-center gap-2 level-{{ project.level }}">
              <div class="whitespace-nowrap overflow-ellipsis overflow-hidden">{{ project.Name }}</div>
            </div>
          </ng-template>
          <ng-template #empty>
            <div class="flex items-center gap-2">
              <i class="text-13 items-center flex fas fa-exclamation-circle"></i>
              <div class="text-sm">Project Not Found</div>
            </div>
          </ng-template>
        </p-autocomplete>
        <small *ngIf="hasError('Parent')" class="text-xs text-red">{{ getError('Parent') }}</small>
      </div>
      @if (isFormationTeam()) {
        <div class="project-detail">
          <label
            >Stage <span class="text-red" *ngIf="fieldRequired('Status')">*</span>
            <i class="fa-regular fa-info-circle" appendTo="body" [pTooltip]="statusTooltip" tooltipPosition="top" [escape]="false" [autoHide]="false"></i>
          </label>
          <p-select appendTo="body" [options]="statusTypes" formControlName="Status"></p-select>
          <small *ngIf="hasError('Status')" class="text-xs text-red">{{ getError('Status') }}</small>
        </div>
      }
      <div class="project-detail">
        <label>Primary Website/Domain <span class="text-red" *ngIf="fieldRequired('Website')">*</span></label>
        <input type="text" pInputText formControlName="Website" />
        <small *ngIf="hasError('Website')" class="text-xs text-red">{{ getError('Website') }}</small>
      </div>
      <div class="project-detail">
        <label>Repository URL <span class="text-red" *ngIf="fieldRequired('RepositoryURL')">*</span></label>
        <input type="text" pInputText formControlName="RepositoryURL" />
        <small *ngIf="hasError('RepositoryURL')" class="text-xs text-red">{{ getError('RepositoryURL') }}</small>
      </div>
      <lfx-file-upload-input
        [form]="form"
        fileControlName="ProjectLogoFile"
        fileLabel="Project Logo"
        placeholder="Upload logo"
        [showToggle]="false"
        accept="image/svg+xml"
        [fileValidators]="[]"></lfx-file-upload-input>
    </div>
  </div>
  <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-9" *ngIf="!isFirstStep">
    <div class="project-detail">
      <div class="flex flex-col lg:flex-row justify-between gap-2">
        <label>Technology Sector <span class="text-red" *ngIf="fieldRequired('Technology')">*</span></label>
        <div class="flex items-center gap-1">
          <label class="cursor-pointer flex items-center gap-2">
            <p-checkbox
              (onChange)="onCrossChange($event, 'Technology')"
              [ngModel]="isCross('Technology')"
              [ngModelOptions]="{ standalone: true }"
              [binary]="true"></p-checkbox>
            Cross-Technology</label
          >
        </div>
      </div>
      <p-multiselect appendTo="body" [options]="technologySectors" formControlName="Technology" placeholder="Select Technology"></p-multiselect>
      <small *ngIf="hasError('Technology')" class="text-xs text-red">{{ getError('Technology') }}</small>
    </div>
    <div class="project-detail">
      <div class="flex flex-col lg:flex-row justify-between gap-2">
        <label>Industry Sector <span class="text-red" *ngIf="fieldRequired('Industry')">*</span></label>
        <div class="flex items-center gap-1">
          <label class="cursor-pointer flex items-center gap-2">
            <p-checkbox
              (onChange)="onCrossChange($event, 'Industry')"
              [ngModel]="isCross('Industry')"
              [ngModelOptions]="{ standalone: true }"
              [binary]="true"></p-checkbox>
            Cross-Industry</label
          >
        </div>
      </div>
      <p-multiselect appendTo="body" [options]="industrySectors" formControlName="Industry" placeholder="Select Industry"></p-multiselect>
      <small *ngIf="hasError('Industry')" class="text-xs text-red">{{ getError('Industry') }}</small>
    </div>

    <div class="project-detail">
      <div class="flex flex-row flex-wrap justify-between gap-2">
        <label
          >Assignment Agreement <span class="text-red" *ngIf="fieldRequired('AssignmentAgreementFile')">*</span>
          <i class="fa-regular fa-info-circle" appendTo="body" [pTooltip]="assignmentAgreementURLTooltip" tooltipPosition="top"></i>
        </label>
        <div class="flex items-center gap-1">
          <label class="cursor-pointer flex items-center gap-2">
            <p-checkbox formControlName="AssignmentAgreementNeeded" [binary]="true"></p-checkbox>
            Not needed?
            <i class="fa-regular fa-info-circle" appendTo="body" [pTooltip]="assignmentAgreementNeededTooltip" tooltipPosition="top"></i>
          </label>
        </div>
      </div>
      <lfx-file-upload-input
        [form]="form"
        fileControlName="AssignmentAgreementFile"
        placeholder="Upload agreement"
        [showToggle]="false"
        [fileValidators]="[]"></lfx-file-upload-input>
    </div>

    @if (isFormationTeam()) {
      <div class="project-detail">
        <label
          >Formation Date
          <span class="text-red" *ngIf="fieldRequired('StartDate')">*</span>
          <i class="fa-regular fa-info-circle" appendTo="body" [pTooltip]="formationDateTooltip" tooltipPosition="top" [escape]="false" [autoHide]="false"></i>
        </label>
        <p-datepicker placeholder="MM/DD/YYYY" formControlName="StartDate"></p-datepicker>
        <small *ngIf="hasError('StartDate')" class="text-xs text-red">{{ getError('StartDate') }}</small>
      </div>
    }
    @if (isFormationTeam()) {
      <div class="project-detail">
        <label>Formation Lead</label>
        <p-autocomplete
          #formationLeadAutocomplete
          formControlName="FormationLead"
          [suggestions]="formationLeadUsers"
          (completeMethod)="onFormationLeadSearch($event)"
          (onClear)="onFormationLeadClear()"
          (onHide)="onFormationLeadPanelHide()"
          appendTo="body"
          [delay]="searchDelay"
          [minLength]="2"
          [completeOnFocus]="true"
          [forceSelection]="true"
          dataKey="ID"
          [showClear]="true"
          placeholder="Search by name..."
          optionLabel="Name">
          <ng-template let-lead #item>
            <div class="flex items-center gap-2">
              <lfx-person-avatar [name]="lead.Name" [photoUrl]="lead.Avatar || lead.LogoURL" [size]="24"></lfx-person-avatar>
              <div class="flex flex-col">
                <span class="text-13 leading-tight">{{ lead.Name }}</span>
                <span class="text-gray-400 text-xs leading-tight">{{ lead.Email || lead.Emails?.[0]?.EmailAddress }}</span>
              </div>
            </div>
          </ng-template>
          <ng-template #empty>
            <div class="flex items-center gap-2">
              <i class="text-13 items-center flex fas fa-exclamation-circle"></i>
              <div class="text-sm">User Not Found</div>
            </div>
          </ng-template>
        </p-autocomplete>
      </div>
    }
    <div class="project-detail">
      <label>Expected Announcement Date <span class="text-red" *ngIf="fieldRequired('ExpectedAnnouncementDate')">*</span></label>
      <p-datepicker placeholder="MM/DD/YYYY" formControlName="ExpectedAnnouncementDate"></p-datepicker>
      <small *ngIf="hasError('ExpectedAnnouncementDate')" class="text-xs text-red">{{ getError('ExpectedAnnouncementDate') }}</small>
    </div>

    <div class="project-detail">
      <div class="flex gap-2">
        <label
          >Technical Activity Type
          <span class="text-red" *ngIf="fieldRequired('TechActivityType')">*</span>
        </label>
      </div>
      <p-multiselect
        appendTo="body"
        [options]="technicalActivityTypes"
        formControlName="TechActivityType"
        placeholder="Select Technical Activity Type"></p-multiselect>
      <small *ngIf="hasError('TechActivityType')" class="text-xs text-red">{{ getError('TechActivityType') }}</small>
    </div>
    <div class="project-detail">
      <label>Software License</label>
      <p-select
        appendTo="body"
        [options]="softwareLicenses"
        formControlName="PrimaryOpenSourceLicense"
        optionLabel="label"
        optionValue="value"
        placeholder="Select Software License"></p-select>
    </div>
    <div class="project-detail">
      <label>Specification License</label>
      <p-select
        appendTo="body"
        [options]="specificationLicenseTypes"
        formControlName="PatentLicense"
        optionLabel="label"
        optionValue="value"
        placeholder="Select Specification License"></p-select>
    </div>

    <div class="project-detail lg:col-span-3">
      <label>Description <span class="text-red" *ngIf="fieldRequired('Description')">*</span></label>
      <textarea rows="2" pTextarea formControlName="Description"></textarea>
      <small *ngIf="hasError('Description')" class="text-xs text-red">{{ getError('Description') }}</small>
    </div>
    <div class="project-detail lg:col-span-3">
      <label
        >Mission Statement
        <span class="text-red" *ngIf="fieldRequired('MissionStatement')">*</span>
        <i
          class="fa-regular fa-info-circle ml-1"
          appendTo="body"
          [pTooltip]="missionStatementTooltip"
          tooltipPosition="top"
          [escape]="false"
          [autoHide]="false"></i
      ></label>
      <textarea rows="2" pTextarea formControlName="MissionStatement"></textarea>
      <small *ngIf="hasError('MissionStatement')" class="text-xs text-red">{{ getError('MissionStatement') }}</small>
    </div>
  </div>
</div>
<div class="flex justify-end gap-9 items-center mt-4">
  <a class="text-black text-13 cursor-pointer" (click)="onCancel()" *ngIf="isFirstStep">Cancel</a>
  <a class="text-black text-13 cursor-pointer" (click)="onPrevious()" *ngIf="!isFirstStep">Back</a>
  <button *ngIf="isFirstStep" pButton type="button" label="Next" class="p-button-primary" (click)="onNext()" [disabled]="isNextDisabled"></button>
  <button
    *ngIf="!isFirstStep"
    pButton
    type="button"
    class="p-button-primary flex items-center gap-2"
    (click)="onCreate()"
    [disabled]="form.invalid || formLoading">
    <span>Create Project</span>
    <i class="fa-light fa-circle-notch fa-spin" *ngIf="formLoading"></i>
  </button>
</div>

<ng-template #inputLoading>
  <p-skeleton height="34px"></p-skeleton>
</ng-template>
